import { Authentication } from '@leedpulse-monorepo/classes';
import Failure from '../../../core/Failure';
import HttpClient from '../../../core/HttpClient';
import { ISubscription } from '@leedpulse-monorepo/interfaces';

// ─── Assign (or reassign) a user to an existing internal subscription ────────

interface AssignUserPayload {
  /** Internal subscription document ID */
  subscription: {id: ISubscription["id"]; api_key: ISubscription["api_key"]} | null;
  /** UID of the user to assign ('none' to unassign) */
  user_uid: string;
  /** Optional: unassign user from this subscription before assigning to the new one (change number flow) */
  old_subscription_id?: string;
  /** Asked to the admin: true hands the previous users' contacts on the number to the new user, false sends them back to their owners */
  transfer_contacts?: boolean;
  /** Optional: the holder the page shows on the number, refused if someone else holds it now */
  old_user_uid?: string;
}

interface AssignUserResponse {
  subscription_id: string;
  user_uid: string;
  sender: string;
  redirection: string;
}

/**
 * Assign a user to an existing internal subscription.
 * Handles reassignment: removes the subscription from the previous user
 * and updates Number.redirection with the new user's phone.
 */
export async function assignUserToSubscription(
  payload: AssignUserPayload,
  auth: Authentication,
): Promise<AssignUserResponse> {
  const iStartedAt = Date.now();
  try {
    if (!payload.subscription) throw new AssignUserFailure('Subscription not provided');

    const body: Record<string, string | boolean> = {
      subscription_id: payload.subscription.id,
      user_uid: payload.user_uid,
    };
    if (payload.old_subscription_id) {
      body.old_subscription_id = payload.old_subscription_id;
    }
    if (payload.transfer_contacts !== undefined) {
      body.transfer_contacts = payload.transfer_contacts;
    }
    if (payload.old_user_uid) {
      body.old_user_uid = payload.old_user_uid;
    }

    const response = await HttpClient.post(
      '/subscription/assign-user',
      body,
      auth.token,
      false,
      {
        'x-api-key': payload.subscription.api_key,
        'x-user-uid': auth.id,
      },
      new Map<number, string>([
        [409, 'Cet utilisateur est déjà assigné à un numéro Leedflow'],
      ])
    );

    return response.data;
  } catch (e) {
    console.error('assignUserToSubscription error:', e);
    // The proxy gave up after 60 s while the backend goes on moving a large set of contacts. Its error page lacks
    // the CORS headers, so the browser usually reports a failed fetch instead of the 504
    const bGatewayTimeout = (e instanceof Error && e.message.includes('Status: 504'))
      || (e instanceof TypeError && Date.now() - iStartedAt > 55000);
    if (bGatewayTimeout) {
      throw new AssignUserFailure('L\'opération prend plus de temps que prévu et se poursuit : rechargez la page dans quelques minutes pour voir le résultat.', 'gateway-timeout');
    } else if (e instanceof TypeError && e.message === 'Failed to fetch') {
      throw new AssignUserFailure('Le serveur ne répond pas');
    } else if (e instanceof Error) {
      throw new AssignUserFailure(e.message);
    }
    return Promise.reject(e);
  }
}

// ─── Failure class ───────────────────────────────────────────────────────────

export class AssignUserFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

